<script setup lang="ts">
  import { computed, ref } from 'vue'
  import { AnalysisWizardModel } from '../../models/AnalysisWizardModel'
  import { FileCheck, FileCogIcon, FileQuestion } from 'lucide-vue-next'
  import FullScreenModal from '@/components/FullScreenModal.vue'
  import DataViewer from '@/components/DataViewer.vue'

  export interface IPostProcessingStepStatusProps {
    wizardModel: AnalysisWizardModel
  }

  const props = defineProps<IPostProcessingStepStatusProps>()

  const dataViewerModalOpen = ref<boolean>(false)

  const viewFileData = async () => {
    if (hasSelectedFile) {
      dataViewerModalOpen.value = true
    }
  }

  const closeDataViewerModal = () => {
    dataViewerModalOpen.value = false
  }

  const hasSelectedFile = computed(
    () => !!(props.wizardModel.sharedModel?.cleanedFile || props.wizardModel.sharedModel?.inputFile)
  )

  const getSelectedFileName = computed(
    () =>
      props.wizardModel.sharedModel?.cleanedFile?.name ||
      props.wizardModel.sharedModel?.inputFile?.name
  )

  const getSelectedFileId = computed(
    () =>
      props.wizardModel.sharedModel?.cleanedFile?.id || props.wizardModel.sharedModel?.inputFile?.id
  )

  const inputDatasetForAnalysis = computed(() => {
    if (props.wizardModel.sharedModel?.cleanedFile != null)
      return props.wizardModel.sharedModel.cleanedFile.name
    else if (props.wizardModel.sharedModel?.inputFile != null)
      return props.wizardModel.sharedModel.inputFile.name
    return 'No data set selected'
  })

  const hasAnalysisBeenExecuted = computed(
    () => props.wizardModel.sharedModel?.postProcessingExecution?.id != null
  )
</script>

<template>
  <a-tooltip>
    <template #title>
      {{ hasSelectedFile ? 'Preview  Data' : 'No file selected for preview' }}
    </template>

    <div class="flex-col" @click="viewFileData" :class="{ 'cursor-pointer': hasSelectedFile }">
      <div class="mb-2 flex items-center">
        <component
          :is="inputDatasetForAnalysis == 'No data set selected' ? FileQuestion : FileCheck"
          class="mr-1 size-6"
          :style="{
            color:
              inputDatasetForAnalysis == 'No data set selected'
                ? 'var(--foreground-80, #9CA3AF)'
                : 'var(--primary, #5aa745)'
          }"
          aria-hidden="true"
        />

        <div class="truncate">
          <b>Input Dataset:</b>
          <span class="ml-1">
            {{ inputDatasetForAnalysis }}
          </span>
        </div>
      </div>
      <div class="flex items-center">
        <component
          :is="FileCogIcon"
          class="mr-1 size-6"
          :style="{
            color: hasAnalysisBeenExecuted
              ? 'var(--primary, #5aa745)'
              : 'var(--foreground-80, #9CA3AF)'
          }"
          aria-hidden="true"
        />

        <span class="self-center truncate">
          <b>Execution Id:</b>
          <span class="ml-1">
            {{
              hasAnalysisBeenExecuted
                ? props.wizardModel.sharedModel?.postProcessingExecution?.id
                : 'Post Processing not executed'
            }}
          </span>
        </span>
      </div>
    </div>
  </a-tooltip>
  <!-- Data Viewer Modal -->
  <FullScreenModal
    :open="dataViewerModalOpen"
    title="Data Viewer"
    :description="getSelectedFileName"
    @close="closeDataViewerModal"
  >
    <DataViewer :fileId="getSelectedFileId || ''" />
  </FullScreenModal>
</template>
